import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import Popup from "reactjs-popup";
import "reactjs-popup/dist/index.css";
import Styles from "./styles.module.scss";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";

import {
    getQuestionnaire,
    saveBlockAnswers,
    setCapsuleQuestionnaireModalStatus
} from "../../../store/cabinet/action";
import {firstOpenBlockIndex, isCapsuleEditable, isQuestionBlockDone} from "../../../helpers/capsule";
import {questionnaireBlockTitle, questionnaireQuestionText} from "../../../helpers/questionnaire";

const CabinetCapsuleQuestionnaireModal = () => {
    const { t, i18n } = useTranslation();
    const dispatch = useDispatch();

    const capsuleQuestionnaireModalStatus = useSelector(store => store.cabinet.capsuleQuestionnaireModalStatus);
    const questionnaire = useSelector(store => store.cabinet.questionnaire);
    const capsuleStats = useSelector(store => store.cabinet.capsuleStats);
    const saveBlockAnswersStarted = useSelector(store => store.cabinet.saveBlockAnswersStarted);

    const [blockIndex, setBlockIndex] = useState(0);
    // черновик ответов текущего блока: {question_id: answer}
    const [answers, setAnswers] = useState({});
    const [isExitConfirmOpen, setIsExitConfirmOpen] = useState(false);

    const bodyRef = useRef(null);
    // позицию подбираем один раз на открытие: дальше блок выбирает пользователь
    const positionedRef = useRef(false);

    const blocks = useMemo(()=> questionnaire?.blocks || [], [questionnaire]);
    const block = blocks[blockIndex];
    const isEditable = isCapsuleEditable(capsuleStats?.capsule_status);

    // сквозная нумерация вопросов — в заголовке блока показан его диапазон
    const blockRange = useMemo(()=>{
        let from = 1;
        for (let i = 0; i < blockIndex; i++){
            from += blocks[i]?.questions?.length || 0;
        }
        return {
            from,
            to: from + (block?.questions?.length || 0) - 1
        };
    }, [blocks, blockIndex, block])

    // Куда уходить после сохранения: сначала ищем непройденный блок дальше по
    // анкете, потом — раньше текущего. Сам текущий в расчёт не берём: его как
    // раз сохраняем, свежие answered/skipped приедут только следующим запросом
    const nextOpenIndex = useMemo(()=>{
        const after = blocks.findIndex((item, index)=> index > blockIndex && !isQuestionBlockDone(item));
        if (after >= 0){
            return after;
        }
        return blocks.findIndex((item, index)=> index < blockIndex && !isQuestionBlockDone(item));
    },[blocks, blockIndex])

    const resetDraft = useCallback((target)=>{
        const draft = {};
        (target?.questions || []).forEach((question)=>{
            draft[question?.id] = question?.answer || '';
        });
        setAnswers(draft);
    },[])

    useEffect(()=>{
        if (capsuleQuestionnaireModalStatus && !questionnaire){
            dispatch(getQuestionnaire());
        }
    },[capsuleQuestionnaireModalStatus, questionnaire, dispatch])

    // Пройденный блок повторно не показываем: на открытии встаём на первый, где
    // есть что заполнять. Пройдено всё — открываем с начала, как просмотр
    useEffect(()=>{
        if (!capsuleQuestionnaireModalStatus){
            positionedRef.current = false;
            return;
        }
        if (positionedRef.current || !blocks.length){
            return;
        }
        positionedRef.current = true;

        const target = firstOpenBlockIndex(blocks);
        setBlockIndex(target < 0 ? 0 : target);
    },[capsuleQuestionnaireModalStatus, blocks])

    // при смене блока (и при первой загрузке данных) поднимаем сохранённые ответы
    useEffect(()=>{
        resetDraft(block);
    },[block, resetDraft])

    const closeModal = () =>{
        setIsExitConfirmOpen(false);
        setBlockIndex(0);
        dispatch(setCapsuleQuestionnaireModalStatus(false));
    }

    const isDirty = () =>{
        return (block?.questions || []).some((question)=>{
            return (answers[question?.id] || '') !== (question?.answer || '');
        });
    }

    const closeHandler = () =>{
        if (isEditable && isDirty()){
            setIsExitConfirmOpen(true);
            return;
        }
        closeModal();
    }

    const scrollTopHandler = () =>{
        if (bodyRef.current){
            bodyRef.current.scrollTop = 0;
        }
    }

    const saveHandler = (successHandler) =>{
        if (!isEditable || saveBlockAnswersStarted || !block?.id){
            return;
        }

        const payload = (block?.questions || []).map((question)=>({
            question_id: question?.id,
            answer: answers[question?.id] ? answers[question?.id] : null
        }));

        dispatch(saveBlockAnswers(block.id, payload, successHandler));
    }

    const goToBlock = (index) =>{
        if (index === blockIndex || index < 0 || index >= blocks.length){
            return;
        }

        const apply = () =>{
            setBlockIndex(index);
            scrollTopHandler();
        };

        // черновик при переходе не теряем: набранное сначала уходит на сервер
        if (isEditable && isDirty()){
            saveHandler(apply);
            return;
        }
        apply();
    }

    const nextBlockHandler = () =>{
        if (nextOpenIndex >= 0){
            setBlockIndex(nextOpenIndex);
            scrollTopHandler();
            return;
        }
        closeModal();
    }

    return (
        <Popup
            open={capsuleQuestionnaireModalStatus}
            modal
            nested
            className="capsuleModal"
            closeOnDocumentClick
            onClose={closeHandler}
        >
            <div className={`${Styles.modalContainer}`}>
                <div className={`${Styles.modalCloseBtn}`} onClick={closeHandler}>
                    <div/>
                    <div/>
                </div>

                <div className={`${Styles.modalContentWrap}`}>
                    <div className={`${Styles.modalHead}`}>
                        <div className={`${Styles.modalTitle}`}>
                            {block
                                ? `${t('Блок')} ${blockIndex + 1}: ${questionnaireBlockTitle(block, i18n.language)} (${blockRange.from}—${blockRange.to})`
                                : t('Анкета')}
                        </div>
                        {blocks.length ? (
                            <div className={`${Styles.blockProgress}`}>
                                <div className={`${Styles.blockProgressBar}`}>
                                    <div
                                        className={`${Styles.blockProgressFill}`}
                                        style={{width: `${((blockIndex + 1) / blocks.length) * 100}%`}}
                                    />
                                </div>
                                <div className={`${Styles.blockProgressName}`}>
                                    {t('Блок')} {blockIndex + 1} {t('из')} {blocks.length}
                                </div>
                            </div>
                        ) : null}

                        {blocks.length > 1 ? (
                            <div className={`${Styles.blockNav}`}>
                                {blocks.map((item, index)=>(
                                    <div
                                        className={`${Styles.blockChip}`
                                            + (index === blockIndex ? ` ${Styles.blockChipOn}` : '')
                                            + (isQuestionBlockDone(item) ? ` ${Styles.blockChipDone}` : '')}
                                        key={'capsule-block_' + item?.id}
                                        title={questionnaireBlockTitle(item, i18n.language)}
                                        onClick={()=>{goToBlock(index)}}
                                    >
                                        {index + 1}
                                    </div>
                                ))}
                            </div>
                        ) : null}
                    </div>

                    <div className={`${Styles.modalBody}`} ref={bodyRef}>
                        {block?.questions?.length ? (
                            <div className={`${Styles.questionsList}`}>
                                {block.questions.map((question, index)=>(
                                    <div className={`${Styles.questionWrap}`} key={'capsule-question_' + question?.id}>
                                        <div className={`${Styles.questionName}`}>
                                            <span>{blockRange.from + index}.</span> {questionnaireQuestionText(question, i18n.language)}
                                        </div>
                                        <input
                                            className={`${Styles.questionInput}`}
                                            type="text"
                                            value={answers[question?.id] || ''}
                                            placeholder={t('Ваш ответ')}
                                            disabled={!isEditable}
                                            maxLength={5000}
                                            onChange={(e)=>{
                                                setAnswers({...answers, [question?.id]: e.target.value});
                                            }}
                                        />
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className={`${Styles.emptyNote}`}>{t('Данных пока нет')}</div>
                        )}
                    </div>

                    <div className={`${Styles.modalFooter}`}>
                        {isEditable ? (
                            <>
                                <div
                                    className={`${Styles.modalBtn} ${saveBlockAnswersStarted ? Styles.modalBtnDsb : null}`}
                                    onClick={()=>{saveHandler(closeModal)}}
                                >
                                    {t('Сохранить')}
                                </div>
                                <div
                                    className={`${Styles.modalBtn} ${saveBlockAnswersStarted ? Styles.modalBtnDsb : null}`}
                                    onClick={()=>{saveHandler(nextBlockHandler)}}
                                >
                                    {nextOpenIndex >= 0 ? t('Сохранить и продолжить') : t('Сохранить и закрыть')}
                                </div>
                            </>
                        ) : (
                            <>
                                <div
                                    className={`${Styles.modalBtn} ${!blockIndex ? Styles.modalBtnDsb : null}`}
                                    onClick={()=>{
                                        if (blockIndex){
                                            setBlockIndex(blockIndex - 1);
                                            scrollTopHandler();
                                        }
                                    }}
                                >
                                    {t('Назад')}
                                </div>
                                <div
                                    className={`${Styles.modalBtn} ${blockIndex + 1 >= blocks.length ? Styles.modalBtnDsb : null}`}
                                    onClick={()=>{
                                        if (blockIndex + 1 < blocks.length){
                                            setBlockIndex(blockIndex + 1);
                                            scrollTopHandler();
                                        }
                                    }}
                                >
                                    {t('Далее')}
                                </div>
                            </>
                        )}
                    </div>
                </div>

                <Popup
                    open={isExitConfirmOpen}
                    modal
                    nested
                    className="capsuleConfirmModal"
                    closeOnDocumentClick
                    onClose={()=>{setIsExitConfirmOpen(false)}}
                >
                    <div className={`${Styles.confirmContainer}`}>
                        <div className={`${Styles.modalCloseBtn}`} onClick={()=>{setIsExitConfirmOpen(false)}}>
                            <div/>
                            <div/>
                        </div>
                        <div className={`${Styles.confirmTitle}`}>{t('Выход из анкеты')}</div>
                        <div className={`${Styles.confirmNote}`}>
                            {t('Если вы выйдете из анкеты без сохранения прогресса, он будет полностью утерян.')}
                        </div>
                        <div className={`${Styles.confirmBtn}`} onClick={()=>{saveHandler(closeModal)}}>
                            {t('Сохранить и выйти')}
                        </div>
                        <div className={`${Styles.confirmBtnDanger}`} onClick={closeModal}>
                            {t('Выйти без сохранений')}
                        </div>
                    </div>
                </Popup>
            </div>
        </Popup>
    );
};

export default CabinetCapsuleQuestionnaireModal;
